/**
 * Meetings Dashboard (default MeetingManagement landing page).
 *
 * A rich, query-driven overview that mirrors the TaskManagement dashboard
 * structure:
 *  - KPI strip (Today / This Week / Upcoming / Total / My Pending Action Items /
 *    Pending Venue Approvals)
 *  - A 2-column grid:
 *      LEFT  → charts: meetings by type (donut), meetings over the next 30 days
 *              (area), meetings by status (bar)
 *      RIGHT → upcoming meetings (next 8, clickable), my open action items,
 *              and quick actions.
 *
 * All meeting numbers derive from a single `getAllViews()` query. Action items
 * and venue bookings are fanned out per-meeting (scoped to the current user's
 * meetings) via `useQueries`, since the SDK exposes them per meeting only.
 *
 * Backend dates are decimal yyyyMMddHHmmss numbers — parsed/formatted via the
 * shared meetingHelpers utilities.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  IconCalendarEvent,
  IconCalendarTime,
  IconCalendarWeek,
  IconClockHour4,
  IconCalendar,
  IconClock,
  IconUser,
  IconUsers,
  IconPlus,
  IconChevronRight,
  IconRefresh,
  IconAlertCircle,
  IconChecklist,
  IconBuildingCommunity,
  IconListCheck,
  IconCalendarStats,
  IconBuilding,
  IconChartArcs,
} from '@tabler/icons-react';
import { LayoutContainer, SimpleGrid } from '@/components/ui/layout';
import {
  Stack,
  Card,
  Group,
  Button,
  Alert,
  Text,
  Title,
  ThemeIcon,
  Paper,
  Grid,
  Badge,
  ActionIcon,
  Divider,
  Skeleton,
} from '@/components/ui';
import { EmptyState } from '@/components/ui/data-display';
import { FadeIn } from '@/components/animations';
import {
  DonutChartWidget,
  AreaChartWidget,
  BarChartWidget,
} from '@/components/advanced/Widgets';
import {
  useAuth,
  useMeetingService,
  useMeetingActionItemService,
  useVenueBookingService,
  type MeetingViewModel,
  type MeetingActionItem,
  type VenueBooking,
} from '@actschurch/ccms-sdk';
import { MeetingStateBadge, MeetingTypeBadge } from '../components';
import { MEETING_QUERY_KEYS } from '../utils/meetingConstants';
import {
  formatTimeRange,
  formatDate,
  getOrganizerName,
  isToday,
  isThisWeek,
  isUpcoming,
  computeMeetingState,
  parseBackendDate,
} from '../utils/meetingHelpers';
import { MEETINGS_ROUTES } from '@/config/routes';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const ACTION_ITEM_STATUS = { OPEN: 0, IN_PROGRESS: 1, DONE: 2 } as const;
const BOOKING_STATUS = { PENDING: 0, CONFIRMED: 1, CANCELLED: 2 } as const;

/** Hex palette so the recharts-based widgets render real fills. */
const CHART_PALETTE = [
  '#228be6', // blue
  '#7048e8', // grape/violet
  '#40c057', // green
  '#fd7e14', // orange
  '#fa5252', // red
  '#15aabf', // cyan
  '#e64980', // pink
  '#82c91e', // lime
  '#fab005', // yellow
  '#12b886', // teal
];

const STATE_HEX: Record<string, string> = {
  Upcoming: '#228be6',
  'In Progress': '#fd7e14',
  Completed: '#12b886',
  Cancelled: '#fa5252',
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function MeetingsDashboard() {
  const navigate = useNavigate();
  const meetingService = useMeetingService();
  const actionItemService = useMeetingActionItemService();
  const venueBookingService = useVenueBookingService();
  const currentUserId = useAuth().state.user?.userId ?? null;

  const {
    data: meetings = [],
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: MEETING_QUERY_KEYS.listView,
    queryFn: () => meetingService.getAllViews(),
  });

  /* ---- Meetings scoped to the current user (organizer OR attendee) ------- */
  const myMeetings = useMemo(() => {
    if (!currentUserId) return [] as MeetingViewModel[];
    return meetings.filter(
      (m) =>
        m.organizerID === currentUserId ||
        (m.meetingAttendees ?? []).some((a) => a.userID === currentUserId)
    );
  }, [meetings, currentUserId]);

  // Bound the per-meeting fan-out: only relevant (non-completed) meetings.
  const myActiveMeetingIds = useMemo(
    () =>
      myMeetings
        .filter((m) => computeMeetingState(m) !== 'Completed')
        .map((m) => m.meetingID)
        .slice(0, 60),
    [myMeetings]
  );

  /* ---- Fan out: open action items assigned to me, across my meetings ----- */
  const actionItemQueries = useQueries({
    queries: myActiveMeetingIds.map((meetingId) => ({
      queryKey: ['meeting', meetingId, 'action-items'],
      queryFn: () => actionItemService.getByMeeting(meetingId),
      enabled: !!currentUserId,
      staleTime: 60_000,
    })),
  });

  const actionItemsLoading = actionItemQueries.some((q) => q.isLoading);

  const myActionItems = useMemo(() => {
    const all: MeetingActionItem[] = [];
    actionItemQueries.forEach((q) => {
      (q.data ?? []).forEach((ai) => all.push(ai));
    });
    return all
      .filter(
        (ai) =>
          ai.assignedToUserId === currentUserId &&
          ai.statusID !== ACTION_ITEM_STATUS.DONE
      )
      .sort((a, b) => (a.dueDate ?? Infinity) - (b.dueDate ?? Infinity));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionItemQueries.map((q) => q.dataUpdatedAt).join(','), currentUserId]);

  /* ---- Fan out: pending venue approvals across my meetings --------------- */
  const bookingQueries = useQueries({
    queries: myActiveMeetingIds.map((meetingId) => ({
      queryKey: ['meeting', meetingId, 'venue-bookings'],
      queryFn: () => venueBookingService.getByMeeting(meetingId),
      enabled: !!currentUserId,
      staleTime: 60_000,
    })),
  });

  const bookingsLoading = bookingQueries.some((q) => q.isLoading);

  const pendingVenueBookings = useMemo(() => {
    const all: VenueBooking[] = [];
    bookingQueries.forEach((q) => {
      (q.data ?? []).forEach((b) => all.push(b));
    });
    return all.filter((b) => b.bookingStatusID === BOOKING_STATUS.PENDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingQueries.map((q) => q.dataUpdatedAt).join(',')]);

  /* ---- KPI stats --------------------------------------------------------- */
  const stats = useMemo(
    () => ({
      today: meetings.filter(isToday).length,
      thisWeek: meetings.filter(isThisWeek).length,
      upcoming: meetings.filter(isUpcoming).length,
      total: meetings.length,
      myActionItems: myActionItems.length,
      pendingVenue: pendingVenueBookings.length,
    }),
    [meetings, myActionItems.length, pendingVenueBookings.length]
  );

  /* ---- Upcoming list (next 8) ------------------------------------------- */
  const upcomingMeetings = useMemo(
    () =>
      meetings
        .filter(isUpcoming)
        .sort((a, b) => {
          const da = parseBackendDate(a.startDate)?.getTime() ?? Infinity;
          const db = parseBackendDate(b.startDate)?.getTime() ?? Infinity;
          return da - db;
        })
        .slice(0, 8),
    [meetings]
  );

  /* ---- Chart: meetings by type (donut) ---------------------------------- */
  const typeChartData = useMemo(() => {
    const counts = new Map<string, number>();
    meetings.forEach((m) => {
      const name = m.typeName || `Type ${m.typeID}`;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({
        name,
        value,
        color: CHART_PALETTE[i % CHART_PALETTE.length],
      }));
  }, [meetings]);

  /* ---- Chart: meetings over the next 30 days (area, by day) ------------- */
  const next30DaysData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const buckets: { date: string; meetings: number }[] = [];
    const indexByKey = new Map<string, number>();

    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      indexByKey.set(key, buckets.length);
      buckets.push({
        date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        meetings: 0,
      });
    }

    meetings.forEach((m) => {
      if (computeMeetingState(m) === 'Cancelled') return;
      const d = parseBackendDate(m.startDate);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const idx = indexByKey.get(key);
      if (idx != null) buckets[idx].meetings += 1;
    });

    return buckets;
  }, [meetings]);

  /* ---- Chart: meetings by status (bar) ---------------------------------- */
  const statusChartData = useMemo(() => {
    const counts = new Map<string, number>();
    meetings.forEach((m) => {
      const name = m.statusName || `Status ${m.statusID}`;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([category, meetingsCount]) => ({ category, meetings: meetingsCount }));
  }, [meetings]);

  const hasAnyMeetings = meetings.length > 0;

  /* ---------------------------------------------------------------------- */
  /* Sub-components                                                          */
  /* ---------------------------------------------------------------------- */

  const KpiTile = ({
    icon,
    color,
    value,
    label,
    onClick,
    loading,
    accent,
  }: {
    icon: React.ReactNode;
    color: string;
    value: number;
    label: string;
    onClick?: () => void;
    loading?: boolean;
    accent?: boolean;
  }) => (
    <Card
      withBorder
      padding="md"
      radius="md"
      shadow="sm"
      style={{
        cursor: onClick ? 'pointer' : undefined,
        borderLeft: accent ? `4px solid var(--mantine-color-${color}-6)` : undefined,
        height: '100%',
      }}
      onClick={onClick}
    >
      <Group wrap="nowrap">
        <ThemeIcon size={44} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0 }}>
          {loading ? (
            <Skeleton height={26} width={32} radius="sm" />
          ) : (
            <Text size="xl" fw={700}>
              {value}
            </Text>
          )}
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lineClamp={1}>
            {label}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  const renderUpcomingRow = (m: MeetingViewModel) => (
    <Paper
      key={m.meetingID}
      withBorder
      p="sm"
      radius="md"
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(MEETINGS_ROUTES.VIEW(m.meetingID))}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
          <Text fw={600} size="sm" lineClamp={1}>
            {m.title || 'Untitled Meeting'}
          </Text>
          <Group gap="xs">
            <MeetingStateBadge meeting={m} size="xs" />
            <MeetingTypeBadge
              typeID={m.typeID}
              typeName={m.typeName}
              typeColor={m.typeColor}
              size="xs"
            />
          </Group>
          <Group gap="lg" wrap="wrap">
            <Group gap={4} wrap="nowrap">
              <IconClock size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {formatTimeRange(m.startDate, m.endDate)}
              </Text>
            </Group>
            <Group gap={4} wrap="nowrap">
              <IconUser size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed" lineClamp={1}>
                {getOrganizerName(m)}
              </Text>
            </Group>
            <Group gap={4} wrap="nowrap">
              <IconUsers size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {m.noOfAttendees ?? 0}
              </Text>
            </Group>
          </Group>
        </Stack>
        <ActionIcon variant="subtle" color="gray" size="md">
          <IconChevronRight size={18} />
        </ActionIcon>
      </Group>
    </Paper>
  );

  const renderActionItemRow = (ai: MeetingActionItem) => {
    const due = ai.dueDate ? parseBackendDate(ai.dueDate) : null;
    const overdue = due ? due.getTime() < Date.now() : false;
    return (
      <Paper
        key={ai.actionItemID}
        withBorder
        p="sm"
        radius="md"
        style={{
          cursor: 'pointer',
          borderLeft: overdue
            ? '3px solid var(--mantine-color-red-5)'
            : '3px solid var(--mantine-color-blue-4)',
        }}
        onClick={() => navigate(MEETINGS_ROUTES.VIEW(ai.meetingID))}
      >
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {ai.title || 'Untitled action item'}
            </Text>
            <Group gap={6}>
              <Badge
                size="xs"
                variant="light"
                color={ai.statusID === ACTION_ITEM_STATUS.IN_PROGRESS ? 'orange' : 'blue'}
              >
                {ai.statusID === ACTION_ITEM_STATUS.IN_PROGRESS ? 'In Progress' : 'Open'}
              </Badge>
              {due && (
                <Text size="xs" c={overdue ? 'red' : 'dimmed'} fw={overdue ? 600 : 400}>
                  Due {formatDate(ai.dueDate)}
                </Text>
              )}
            </Group>
          </Stack>
          <ActionIcon variant="subtle" color="gray" size="sm">
            <IconChevronRight size={16} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  };

  /* ---------------------------------------------------------------------- */
  /* Loading skeleton                                                        */
  /* ---------------------------------------------------------------------- */

  if (isPending) {
    return (
      <LayoutContainer>
        <FadeIn>
          <Stack gap="lg">
            <Group justify="space-between" wrap="wrap">
              <Stack gap={4}>
                <Skeleton height={28} width={180} radius="sm" />
                <Skeleton height={14} width={240} radius="sm" />
              </Stack>
              <Skeleton height={36} width={180} radius="sm" />
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} height={84} radius="md" />
              ))}
            </SimpleGrid>
            <Grid>
              <Grid.Col span={{ base: 12, lg: 7 }}>
                <Skeleton height={520} radius="md" />
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 5 }}>
                <Skeleton height={520} radius="md" />
              </Grid.Col>
            </Grid>
          </Stack>
        </FadeIn>
      </LayoutContainer>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ---------------------------------------------------------------------- */

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" wrap="wrap">
            <Stack gap={0}>
              <Title order={2}>Meetings Dashboard</Title>
              <Text c="dimmed" size="sm">
                Schedule, track, and act on your meetings
              </Text>
            </Stack>
            <Group gap="sm">
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={16} />}
                onClick={() => refetch()}
                loading={isFetching}
              >
                Refresh
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => navigate(MEETINGS_ROUTES.CREATE)}
              >
                Create Meeting
              </Button>
            </Group>
          </Group>

          {/* KPI strip */}
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
            <KpiTile
              icon={<IconCalendarTime size={22} />}
              color="orange"
              value={stats.today}
              label="Today"
              onClick={() => navigate(MEETINGS_ROUTES.CALENDAR)}
            />
            <KpiTile
              icon={<IconCalendarWeek size={22} />}
              color="grape"
              value={stats.thisWeek}
              label="This Week"
              onClick={() => navigate(MEETINGS_ROUTES.LIST)}
            />
            <KpiTile
              icon={<IconClockHour4 size={22} />}
              color="blue"
              value={stats.upcoming}
              label="Upcoming"
              onClick={() => navigate(MEETINGS_ROUTES.LIST)}
            />
            <KpiTile
              icon={<IconCalendarEvent size={22} />}
              color="teal"
              value={stats.total}
              label="Total"
              onClick={() => navigate(MEETINGS_ROUTES.LIST)}
            />
            <KpiTile
              icon={<IconListCheck size={22} />}
              color="indigo"
              value={stats.myActionItems}
              label="My Action Items"
              loading={actionItemsLoading}
              accent={stats.myActionItems > 0}
            />
            <KpiTile
              icon={<IconBuildingCommunity size={22} />}
              color="red"
              value={stats.pendingVenue}
              label="Venue Approvals"
              loading={bookingsLoading}
              accent={stats.pendingVenue > 0}
            />
          </SimpleGrid>

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Error loading meetings"
            >
              {error instanceof Error ? error.message : 'Failed to load meetings'}
            </Alert>
          )}

          {!hasAnyMeetings && !error ? (
            <Card withBorder padding="xl" radius="md">
              <EmptyState
                icon={<IconCalendarEvent size={32} />}
                title="No meetings yet"
                description="Create your first meeting to start tracking schedules, attendees, and action items."
                actionLabel="Create Meeting"
                onAction={() => navigate(MEETINGS_ROUTES.CREATE)}
              />
            </Card>
          ) : (
            <Grid>
              {/* LEFT: charts */}
              <Grid.Col span={{ base: 12, lg: 7 }}>
                <Stack gap="lg">
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      {typeChartData.length > 0 ? (
                        <DonutChartWidget
                          title="Meetings by Type"
                          subtitle={`${meetings.length} total`}
                          data={typeChartData}
                          height={260}
                          showExport={false}
                          centerLabel={String(meetings.length)}
                        />
                      ) : (
                        <Card withBorder p="lg" radius="md" h="100%">
                          <Stack gap="xs">
                            <Group gap="xs">
                              <IconChartArcs size={18} />
                              <Text fw={600}>Meetings by Type</Text>
                            </Group>
                            <EmptyState title="No data" size="sm" />
                          </Stack>
                        </Card>
                      )}
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      {statusChartData.length > 0 ? (
                        <BarChartWidget
                          title="Meetings by Status"
                          subtitle="Across all meetings"
                          data={statusChartData}
                          xAxisKey="category"
                          bars={[
                            { dataKey: 'meetings', name: 'Meetings', color: CHART_PALETTE[0] },
                          ]}
                          height={260}
                          showLegend={false}
                          showExport={false}
                        />
                      ) : (
                        <Card withBorder p="lg" radius="md" h="100%">
                          <Stack gap="xs">
                            <Group gap="xs">
                              <IconCalendarStats size={18} />
                              <Text fw={600}>Meetings by Status</Text>
                            </Group>
                            <EmptyState title="No data" size="sm" />
                          </Stack>
                        </Card>
                      )}
                    </Grid.Col>
                  </Grid>

                  <AreaChartWidget
                    title="Meetings — Next 30 Days"
                    subtitle="Scheduled meetings per day"
                    data={next30DaysData}
                    xAxisKey="date"
                    areas={[
                      {
                        dataKey: 'meetings',
                        name: 'Meetings',
                        color: STATE_HEX.Upcoming,
                        fillOpacity: 0.25,
                      },
                    ]}
                    height={280}
                    showLegend={false}
                    showExport={false}
                  />
                </Stack>
              </Grid.Col>

              {/* RIGHT: upcoming + action items + quick actions */}
              <Grid.Col span={{ base: 12, lg: 5 }}>
                <Stack gap="lg">
                  {/* Upcoming meetings */}
                  <Card withBorder padding="lg" radius="md">
                    <Group justify="space-between" align="center" mb="sm">
                      <Group gap="xs">
                        <IconCalendarEvent size={20} />
                        <Title order={4}>Upcoming Meetings</Title>
                      </Group>
                      <Button
                        variant="subtle"
                        size="xs"
                        rightSection={<IconChevronRight size={14} />}
                        onClick={() => navigate(MEETINGS_ROUTES.LIST)}
                      >
                        View all
                      </Button>
                    </Group>
                    <Divider mb="sm" />
                    {upcomingMeetings.length === 0 ? (
                      <EmptyState
                        icon={<IconCalendarEvent size={28} />}
                        title="No upcoming meetings"
                        actionLabel="Schedule a meeting"
                        onAction={() => navigate(MEETINGS_ROUTES.CREATE)}
                        size="sm"
                      />
                    ) : (
                      <Stack gap="sm">{upcomingMeetings.map(renderUpcomingRow)}</Stack>
                    )}
                  </Card>

                  {/* My action items */}
                  <Card withBorder padding="lg" radius="md">
                    <Group justify="space-between" align="center" mb="sm">
                      <Group gap="xs">
                        <IconChecklist size={20} />
                        <Title order={4}>My Action Items</Title>
                        {myActionItems.length > 0 && (
                          <Badge size="sm" color="indigo" variant="filled">
                            {myActionItems.length}
                          </Badge>
                        )}
                      </Group>
                    </Group>
                    <Divider mb="sm" />
                    {actionItemsLoading ? (
                      <Stack gap="sm">
                        {[1, 2, 3].map((i) => (
                          <Skeleton key={i} height={58} radius="md" />
                        ))}
                      </Stack>
                    ) : myActionItems.length === 0 ? (
                      <EmptyState
                        icon={<IconListCheck size={28} />}
                        title="No open action items"
                        description="Action items assigned to you from your meetings will show here."
                        size="sm"
                      />
                    ) : (
                      <Stack gap="sm">
                        {myActionItems.slice(0, 8).map(renderActionItemRow)}
                        {myActionItems.length > 8 && (
                          <Text size="xs" c="dimmed" ta="center">
                            +{myActionItems.length - 8} more
                          </Text>
                        )}
                      </Stack>
                    )}
                  </Card>

                  {/* Quick actions */}
                  <Card withBorder padding="lg" radius="md">
                    <Title order={4} mb="sm">
                      Quick Actions
                    </Title>
                    <Divider mb="sm" />
                    <SimpleGrid cols={2} spacing="sm">
                      <Button
                        variant="light"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => navigate(MEETINGS_ROUTES.CREATE)}
                      >
                        New Meeting
                      </Button>
                      <Button
                        variant="light"
                        color="grape"
                        leftSection={<IconCalendar size={16} />}
                        onClick={() => navigate(MEETINGS_ROUTES.CALENDAR)}
                      >
                        Calendar
                      </Button>
                      <Button
                        variant="light"
                        color="orange"
                        leftSection={<IconBuilding size={16} />}
                        onClick={() => navigate(MEETINGS_ROUTES.CREATE)}
                      >
                        Book a Venue
                      </Button>
                      <Button
                        variant="light"
                        color="teal"
                        leftSection={<IconUser size={16} />}
                        onClick={() => navigate(MEETINGS_ROUTES.MY_MEETINGS)}
                      >
                        My Meetings
                      </Button>
                    </SimpleGrid>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
